'use client';

/* ──────────────────────────────────────────────
 *  Sélecteurs au-dessus du calendrier (spec Parcours P3 § 7.2-7.4) :
 *  lieu en parcours express, membre (« Sans préférence » d'abord,
 *  « Choisir par prestation » en multi), ressource. Pas d'écran ni
 *  de « Continuer » : chaque choix vide le créneau retenu et la grille
 *  se recharge, le client voit l'effet tout de suite.
 * ────────────────────────────────────────────── */

import { type JSX, useEffect, useMemo, useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { isMemberChoiceSkippable, placeChipGroups } from '../../context/booking-machine';
import {
  cartItemNeedsMember,
  getRequiredMemberCount,
  memberDisplayName,
  pickedMemberIds,
} from '../../utils/member-eligibility';
import { isMemberPlaceCompatible } from '../../utils/place-compat';
import type { PlaceGroupKey } from '../../utils/place-groups';
import type { CartItem, MemberAssignment, ResourceAssignment, SelectableResource, TeamMember } from '../../types';

/* Durée effective d'un item (prix sélectionné + suppléments cochés), pour la sonde d'éligibilité. */
function getItemDuration(item: CartItem): number {
  const supplementDuration = item.supplements
    .filter((s): boolean => s.selected)
    .reduce((sum, s): number => sum + (s.duration_minutes ?? 0), 0);
  return item.selectedPrice.duration_minutes + supplementDuration;
}

/* Membre qualifié pour un article : `team_member_ids` vide = tout le monde, libre-service = aucune contrainte. */
function isQualifiedForItem(memberId: number, item: CartItem): boolean {
  if ((item.prestation.required_team_members_count ?? 1) === 0) return true;
  const ids = item.prestation.team_member_ids;
  return !ids || ids.length === 0 || ids.includes(memberId);
}

/* Éligibilité « chaîne » par position du panier : ensemble des membres pouvant servir la position
   dans une combinaison réservable. unconstrained ⇒ ne rien griser pour cette position. */
type ChainEligibility = Map<number, { ids: Set<number>; unconstrained: boolean }>;

/* Mode global : la même sélection s'applique à toutes les lignes du panier qui ont besoin d'un intervenant,
   tronquée au nombre de membres que chaque prestation exige ; une ligne en libre-service n'en reçoit aucun.
   `selectedIds` vide = sans préférence. */
function buildGlobalAssignments(cartItems: CartItem[], selectedIds: number[]): MemberAssignment[] {
  return cartItems.map((item, i): MemberAssignment => {
    if (!cartItemNeedsMember(item)) {
      return { prestationId: item.prestation.prestation_id, cartIndex: i, memberId: undefined, memberIds: undefined };
    }
    const required = getRequiredMemberCount(item);
    return {
      prestationId: item.prestation.prestation_id,
      cartIndex: i,
      memberId: selectedIds[0],
      /* `undefined` et non `[]` quand il n'y a rien à affecter : c'est la forme que consomme la machine
         pour « sans préférence ». */
      memberIds: required === 1 || selectedIds.length === 0 ? undefined : selectedIds.slice(0, required),
    };
  });
}

/* Sélection envoyable : chaque article a « sans préférence » ou exactement ses N membres. */
const isCompleteAssignment = (cartItems: CartItem[], assignments: MemberAssignment[]): boolean =>
  cartItems.every((item, i): boolean => {
    const picked = pickedMemberIds(assignments.find((a): boolean => a.cartIndex === i)).length;
    return picked === 0 || picked === getRequiredMemberCount(item);
  });

const assignmentKey = (assignments: MemberAssignment[]): string =>
  assignments.map((a): string => `${a.cartIndex}:${pickedMemberIds(a).join('-')}`).join('|');

function toggle(ids: number[], memberId: number, required: number): number[] | undefined {
  if (ids.includes(memberId)) return ids.filter((id): boolean => id !== memberId);
  if (required === 1) return [memberId];
  if (ids.length < required) return [...ids, memberId];
  return undefined;
}

function MemberChip({
  member,
  selected,
  disabled,
  badge,
  onClick,
}: {
  member: TeamMember;
  selected: boolean;
  disabled?: boolean;
  badge?: string;
  onClick: () => void;
}): JSX.Element {
  const picture = member.picture?.[0]?.small_url;
  return (
    <button
      type="button"
      className={`bt-chip ${selected ? 'bt-chip--selected' : ''}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
    >
      {picture ? (
        <img className="bt-chip__avatar" src={picture} alt="" />
      ) : (
        <span className="bt-chip__avatar bt-chip__avatar--initial" aria-hidden="true">
          {memberDisplayName(member).charAt(0).toUpperCase()}
        </span>
      )}
      {memberDisplayName(member)}
      {badge ? <span className="bt-chip__badge">{badge}</span> : null}
    </button>
  );
}

function PlaceChips(): JSX.Element | null {
  const { ctx, send, labels } = useBooking();
  const groups = useMemo(() => placeChipGroups(ctx), [ctx]);
  if (groups.length === 0) return null;
  const groupTitle: Record<PlaceGroupKey, string> = {
    establishment: labels.place_group_establishment,
    remote: labels.place_group_remote,
    traveling: labels.place_group_traveling,
  };
  return (
    <div className="bt-selector">
      <span className="bt-selector__label">{labels.slot_place_label}</span>
      <div className="bt-selector__groups">
        {groups.map(
          (group): JSX.Element => (
            <div key={group.key} className="bt-selector__group" role="group" aria-label={groupTitle[group.key]}>
              {groups.length > 1 && <span className="bt-selector__group-title">{groupTitle[group.key]}</span>}
              <div className="bt-selector__chips">
                {group.places.map((place): JSX.Element => {
                  const selected = ctx.selectedPlaceId === place.pro_place_id;
                  return (
                    <button
                      key={place.pro_place_id}
                      type="button"
                      className={`bt-chip ${selected ? 'bt-chip--selected' : ''}`}
                      aria-pressed={selected}
                      onClick={(): void => {
                        if (!selected) send({ type: 'SELECT_PLACE', placeId: place.pro_place_id });
                      }}
                    >
                      {place.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

function MemberPicker(): JSX.Element | null {
  const { ctx, send, labels, params, callbacks } = useBooking();
  const { cartItems, availableMembers, selectedPlaceId, memberAssignments } = ctx;
  const multiMemberAllowed = params.allow_multi_member !== false;
  const skippable = useMemo((): boolean => isMemberChoiceSkippable(ctx), [ctx]);

  /* Brouillon local : un choix à plusieurs membres ne part qu'une fois complet. */
  const [draft, setDraft] = useState<MemberAssignment[]>(memberAssignments);
  const committedKey = assignmentKey(memberAssignments);
  useEffect((): void => setDraft(memberAssignments), [committedKey]);

  /* Lignes qui demandent un intervenant, avec leur position dans le panier : une prestation en libre-service
     n'apparaît pas dans ce choix (cf. cartItemNeedsMember). */
  const memberLines = useMemo(
    (): Array<{ item: CartItem; cartIndex: number }> =>
      cartItems
        .map((item, cartIndex): { item: CartItem; cartIndex: number } => ({ item, cartIndex }))
        .filter(({ item }): boolean => cartItemNeedsMember(item)),
    [cartItems],
  );

  const sameMembersEverywhere = useMemo((): boolean => {
    if (memberLines.length <= 1) return true;
    const first = memberLines[0].item.prestation.team_member_ids;
    if (!first) return true;
    return memberLines.every(({ item }): boolean => {
      const ids = item.prestation.team_member_ids;
      if (!ids) return true;
      return ids.length === first.length && ids.every((id): boolean => first.includes(id));
    });
  }, [memberLines]);
  const [perService, setPerService] = useState(!sameMembersEverywhere && multiMemberAllowed);

  /* Sonde « chaîne » (mode par prestation, multi-prestation) : grise les membres sans combinaison réservable. */
  const [chainEligibility, setChainEligibility] = useState<ChainEligibility>((): ChainEligibility => new Map());
  const draftKey = assignmentKey(draft);
  useEffect((): (() => void) => {
    const check = callbacks.onCheckEligibleMembers;
    if (!check || !perService || cartItems.length < 2 || selectedPlaceId === undefined) {
      setChainEligibility(new Map());
      return (): void => {
        /* rien à nettoyer */
      };
    }
    const controller = new AbortController();
    const timer = setTimeout((): void => {
      check({
        slots: cartItems.map(
          (
            item,
            i,
          ): {
            prestation_id: number;
            pro_place_id: number;
            duration: number;
            quantity: number;
            team_member_id?: number;
          } => {
            const picked = pickedMemberIds(draft.find((a): boolean => a.cartIndex === i));
            // Position mono-membre déjà choisie ⇒ on la fige ; sinon « sans préférence » (collectée).
            const fixed = getRequiredMemberCount(item) === 1 ? picked[0] : undefined;
            return {
              prestation_id: item.prestation.prestation_id,
              pro_place_id: selectedPlaceId,
              duration: getItemDuration(item),
              quantity: item.quantity,
              ...(fixed !== undefined ? { team_member_id: fixed } : {}),
            };
          },
        ),
        signal: controller.signal,
      })
        .then((positions): void => {
          const next: ChainEligibility = new Map();
          for (const pos of positions) {
            next.set(pos.index, { ids: new Set(pos.eligible_member_ids), unconstrained: pos.unconstrained });
          }
          setChainEligibility(next);
        })
        .catch((): void => {
          /* annulé (debounce) ou erreur réseau — on garde l'éligibilité courante */
        });
    }, 300);
    return (): void => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [perService, selectedPlaceId, cartItems, draftKey, callbacks]);

  /* Membres remisés (lot 4) : portés par la promotion des cartes du panier. */
  const memberBadges = useMemo((): Map<number, string> => {
    const badges = new Map<number, string>();
    for (const item of cartItems) {
      const promotion = item.prestation.promotion;
      for (const id of promotion?.team_member_ids ?? []) {
        if (!badges.has(id) && promotion) badges.set(id, promotion.value_label);
      }
    }
    return badges;
  }, [cartItems]);

  const membersAtPlace = useMemo(
    (): TeamMember[] =>
      availableMembers.filter((member): boolean => isMemberPlaceCompatible(member.pro_place_ids, selectedPlaceId)),
    [availableMembers, selectedPlaceId],
  );

  if (skippable) return null;

  const commit = (next: MemberAssignment[]): void => {
    setDraft(next);
    if (isCompleteAssignment(cartItems, next) && assignmentKey(next) !== committedKey) {
      send({ type: 'SET_MEMBERS', assignments: next });
    }
  };

  const maxRequired = memberLines.reduce((max, { item }): number => Math.max(max, getRequiredMemberCount(item)), 1);
  // Sélection globale lue sur la 1re ligne avec intervenant (une ligne en libre-service n'en porte jamais).
  const globalIds = pickedMemberIds(draft.find((a): boolean => a.cartIndex === memberLines[0]?.cartIndex));
  const noPreference = globalIds.length === 0;
  const remaining = Math.max(0, maxRequired - globalIds.length);

  /* « Sans préférence » : la remise dépend du membre attribué (décision 2 du plan, l'affectation ne change pas). */
  const discountedNames = membersAtPlace
    .filter((member): boolean => memberBadges.has(member.team_member_id))
    .map(memberDisplayName);
  const firstBadge = [...memberBadges.values()][0];
  const noPreferenceNote =
    noPreference && !perService && discountedNames.length > 0 && firstBadge
      ? labels.member_promotion_no_preference
          .replace('{value}', firstBadge)
          .replace('{names}', discountedNames.join(', '))
      : undefined;

  const globalMembers = membersAtPlace.filter((member): boolean =>
    memberLines.every(({ item }): boolean => isQualifiedForItem(member.team_member_id, item)),
  );

  return (
    <div className="bt-selector">
      <span className="bt-selector__label">{labels.slot_member_label}</span>
      {!perService ? (
        <>
          <div className="bt-selector__chips" role="group" aria-label={labels.choose_member}>
            <button
              type="button"
              className={`bt-chip ${noPreference ? 'bt-chip--selected' : ''}`}
              aria-pressed={noPreference}
              onClick={(): void => commit(buildGlobalAssignments(cartItems, []))}
            >
              {labels.no_preference}
            </button>
            {globalMembers.map(
              (member): JSX.Element => (
                <MemberChip
                  key={member.team_member_id}
                  member={member}
                  selected={globalIds.includes(member.team_member_id)}
                  badge={memberBadges.get(member.team_member_id)}
                  onClick={(): void => {
                    const next = toggle(globalIds, member.team_member_id, maxRequired);
                    if (next) commit(buildGlobalAssignments(cartItems, next));
                  }}
                />
              ),
            )}
          </div>
          {maxRequired >= 2 && !noPreference && remaining > 0 ? (
            <p className="bt-selector__hint">{labels.pick_n_members_remaining.replace('{n}', String(remaining))}</p>
          ) : null}
          {noPreferenceNote ? <p className="bt-selector__hint">{noPreferenceNote}</p> : null}
          {memberLines.length > 1 && multiMemberAllowed && (
            <button type="button" className="bt-link-subtle" onClick={(): void => setPerService(true)}>
              {labels.choose_per_service}
            </button>
          )}
        </>
      ) : (
        <div className="bt-selector__rows">
          {memberLines.map(({ item, cartIndex }): JSX.Element => {
            const required = getRequiredMemberCount(item);
            const picked = pickedMemberIds(draft.find((a): boolean => a.cartIndex === cartIndex));
            const eligibility = chainEligibility.get(cartIndex);
            const itemRemaining = Math.max(0, required - picked.length);
            const setItem = (ids: number[]): void =>
              commit(
                draft.map(
                  (a): MemberAssignment =>
                    a.cartIndex === cartIndex
                      ? { ...a, memberId: ids[0], memberIds: required === 1 || ids.length === 0 ? undefined : ids }
                      : a,
                ),
              );
            return (
              <div key={`${item.prestation.prestation_id}-${cartIndex}`} className="bt-selector__row">
                <span className="bt-selector__row-title">
                  {item.prestation.name}
                  {item.quantity > 1 && ` x${item.quantity}`}
                </span>
                <div className="bt-selector__chips" role="group" aria-label={item.prestation.name}>
                  <button
                    type="button"
                    className={`bt-chip ${picked.length === 0 ? 'bt-chip--selected' : ''}`}
                    aria-pressed={picked.length === 0}
                    onClick={(): void => setItem([])}
                  >
                    {labels.no_preference}
                  </button>
                  {membersAtPlace
                    .filter((member): boolean => isQualifiedForItem(member.team_member_id, item))
                    .map((member): JSX.Element => {
                      const selected = picked.includes(member.team_member_id);
                      /* On ne grise jamais un membre déjà sélectionné pour cette position. */
                      const chainOk =
                        !eligibility || eligibility.unconstrained || eligibility.ids.has(member.team_member_id);
                      return (
                        <MemberChip
                          key={member.team_member_id}
                          member={member}
                          selected={selected}
                          disabled={!selected && !chainOk}
                          badge={memberBadges.get(member.team_member_id)}
                          onClick={(): void => {
                            const next = toggle(picked, member.team_member_id, required);
                            if (next) setItem(next);
                          }}
                        />
                      );
                    })}
                </div>
                {required >= 2 && picked.length > 0 && itemRemaining > 0 ? (
                  <p className="bt-selector__hint">
                    {labels.pick_n_members_remaining.replace('{n}', String(itemRemaining))}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Une ressource est disponible au lieu choisi si elle n'a aucune contrainte de lieu ou si le lieu y figure. */
const resourceAtPlace = (resource: SelectableResource, placeId: number | undefined): boolean =>
  !resource.pro_place_ids ||
  resource.pro_place_ids.length === 0 ||
  (placeId !== undefined && resource.pro_place_ids.includes(placeId));

function ResourcePicker(): JSX.Element | null {
  const { ctx, send, labels } = useBooking();
  const { cartItems, selectedPlaceId, resourceAssignments } = ctx;
  const groups = cartItems
    .map((item, cartIndex): { item: CartItem; cartIndex: number; resources: SelectableResource[] } => ({
      item,
      cartIndex,
      resources: (item.prestation.selectable_resources ?? []).filter((r): boolean =>
        resourceAtPlace(r, selectedPlaceId),
      ),
    }))
    /* Une seule ressource au lieu : déjà retenue d'office, rien à choisir. */
    .filter((group): boolean => group.resources.length > 1);
  if (groups.length === 0) return null;

  const select = (cartIndex: number, proResourceId: number): void =>
    send({
      type: 'SET_RESOURCES',
      assignments: resourceAssignments.map(
        (a): ResourceAssignment => (a.cartIndex === cartIndex ? { ...a, pro_resource_id: proResourceId } : a),
      ),
    });

  return (
    <div className="bt-selector">
      <span className="bt-selector__label">{labels.slot_resource_label}</span>
      <div className="bt-selector__rows">
        {groups.map(({ item, cartIndex, resources }): JSX.Element => {
          const current = resourceAssignments.find((a): boolean => a.cartIndex === cartIndex)?.pro_resource_id;
          return (
            <div key={`${item.prestation.prestation_id}-${cartIndex}`} className="bt-selector__row">
              {cartItems.length > 1 && <span className="bt-selector__row-title">{item.prestation.name}</span>}
              <div className="bt-selector__chips" role="group" aria-label={labels.choose_resource}>
                {resources.map((resource): JSX.Element => {
                  const selected = current === resource.pro_resource_id;
                  return (
                    <button
                      key={resource.pro_resource_id}
                      type="button"
                      className={`bt-chip ${selected ? 'bt-chip--selected' : ''}`}
                      aria-pressed={selected}
                      title={resource.description ?? undefined}
                      onClick={(): void => {
                        if (!selected) select(cartIndex, resource.pro_resource_id);
                      }}
                    >
                      {resource.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SlotSelectors(): JSX.Element {
  return (
    <div className="bt-selectors">
      <PlaceChips />
      <MemberPicker />
      <ResourcePicker />
    </div>
  );
}
